'use client';

import { forwardRef } from 'react';
import type { InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';

// DESIGN.md §8.4 — sunken bg, no rest border but a 1px top-edge inset rim
// for tactile depth, plus an ambient hero-glow halo so inputs share the
// brand language of the primary button. Focus stacks an inset 1.5px
// hero-start ring and a stronger glow over the same wash tint.
const BASE =
  'rounded-sm bg-sunken px-3.5 py-2.5 min-h-10 text-sm text-fg-primary placeholder:text-fg-muted ' +
  'outline-none transition-shadow duration-200 ease-[cubic-bezier(0.34,1.56,0.64,1)] ' +
  'shadow-[inset_0_1px_2px_rgba(0,0,0,0.06),0_3px_12px_var(--hero-glow)] ' +
  'dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_3px_12px_var(--hero-glow)] ' +
  'focus:bg-hero-wash ' +
  'focus:shadow-[inset_0_0_0_1.5px_var(--hero-start),0_6px_18px_var(--hero-glow)] ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

/**
 * Default text input. DESIGN.md §8.4 — sunken background, no border at
 * rest, focus ring with hero wash. Pass `className` to add modifiers
 * (e.g. `font-mono`, `tracking-widest`).
 */
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={`${BASE} ${className ?? ''}`} {...rest} />;
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...rest }, ref) {
    return <textarea ref={ref} className={`${BASE} ${className ?? ''}`} {...rest} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <select ref={ref} className={`${BASE} pr-8 ${className ?? ''}`} {...rest}>
        {children}
      </select>
    );
  },
);

/**
 * A vertically-stacked label. Common form-row pattern across the app.
 */
export function Field({
  label,
  hint,
  children,
  className,
}: {
  label: React.ReactNode;
  hint?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={'flex flex-col gap-1 text-sm ' + (className ?? '')}>
      <span className="font-medium text-fg-primary">
        {label}
        {hint && <span className="ml-1 font-normal text-fg-muted">{hint}</span>}
      </span>
      {children}
    </label>
  );
}
